import { z } from 'zod';

/**
 * The material vocabulary suppliers declare against a casting/molding or
 * forming process.
 *
 * Casting, molding and forming suppliers do not think in Senvol part numbers —
 * they think in classes ("we run nylons", "we form stainless"). Senvol's
 * catalogue is built for additive and carries almost nothing for these
 * processes, so the picker offers this closed list instead of a material search.
 *
 * FAMILY GATE. Which list a process draws from is a property of the process,
 * not of the family it sits under: `mfg_casting_molding` holds both polymer
 * molding (injection, urethane, rotational) and metal casting (investment, die,
 * centrifugal, permanent mold). Every node therefore carries its own
 * `materialClassFamily`; see the `capability_nodes.material_class_family`
 * column.
 *
 * SUBCLASSES. Each class may grow an optional subclass tier ("Aluminum alloys"
 * → "6061", "A380") without invalidating anything already stored: a selection
 * is `(classId, subclassId?)` and a row saved before its class had subclasses
 * simply has a null subclass, which reads as "the class as a whole". Nothing
 * keys on `subclasses` being non-empty, so adding one is a pure append.
 *
 * Shared by the API (validation, persistence) and both portals (labels), so the
 * ids stored in `storefront_capability_spec_material_classes.material_class`
 * always mean the same thing on both sides.
 */

export const MATERIAL_CLASS_FAMILIES = ['polymer', 'metal'] as const;

export type MaterialClassFamily = (typeof MATERIAL_CLASS_FAMILIES)[number];

export const materialClassFamilySchema = z.enum(MATERIAL_CLASS_FAMILIES);

export interface MaterialSubclassDef {
  id: string;
  label: string;
}

export interface MaterialClassDef {
  id: string;
  label: string;
  /**
   * Finer grades within the class. Empty today for every class; a selection
   * carries `subclassId: null` and means the whole class, so filling this in
   * later leaves existing rows valid.
   */
  subclasses: readonly MaterialSubclassDef[];
}

/**
 * The escape hatch, present in every family. Carries a free-text label instead
 * of a subclass — the supplier names the material themselves.
 */
export const OTHER_MATERIAL_CLASS_ID = 'other_exotic';

const OTHER_MATERIAL_CLASS: MaterialClassDef = {
  id: OTHER_MATERIAL_CLASS_ID,
  label: 'Other / Exotic',
  subclasses: [],
};

/** Casting & molding, polymer processes. */
export const POLYMER_MATERIAL_CLASSES: readonly MaterialClassDef[] = [
  { id: 'polyolefin', label: 'Commodity polyolefins (PP, PE/HDPE/LDPE)', subclasses: [] },
  { id: 'abs', label: 'ABS', subclasses: [] },
  { id: 'polycarbonate', label: 'Polycarbonate (PC) & PC blends (PC/ABS)', subclasses: [] },
  { id: 'polyamide', label: 'Nylons / Polyamides (PA6, PA66, glass-filled)', subclasses: [] },
  { id: 'acetal', label: 'Acetal / POM (Delrin)', subclasses: [] },
  { id: 'polyurethane', label: 'Polyurethanes (PU — cast & molded)', subclasses: [] },
  {
    id: 'thermoplastic_elastomer',
    label: 'Thermoplastic elastomers (TPE/TPU/TPV)',
    subclasses: [],
  },
  { id: 'silicone', label: 'Silicones (LSR)', subclasses: [] },
  { id: 'acrylic_styrenic', label: 'Acrylics (PMMA) & styrenics (PS, SAN)', subclasses: [] },
  {
    id: 'high_performance_thermoplastic',
    label: 'High-performance thermoplastics (PEEK, PEI/Ultem, PPS, PSU)',
    subclasses: [],
  },
  OTHER_MATERIAL_CLASS,
];

/** Forming & deformation, and the metal casting processes. */
export const METAL_MATERIAL_CLASSES: readonly MaterialClassDef[] = [
  { id: 'carbon_alloy_steel', label: 'Carbon & alloy steels', subclasses: [] },
  { id: 'stainless_steel', label: 'Stainless steels', subclasses: [] },
  { id: 'tool_steel', label: 'Tool steels', subclasses: [] },
  { id: 'cast_iron', label: 'Cast iron', subclasses: [] },
  { id: 'aluminum_alloy', label: 'Aluminum alloys', subclasses: [] },
  { id: 'copper_alloy', label: 'Copper alloys (brass, bronze)', subclasses: [] },
  {
    id: 'nickel_superalloy',
    label: 'Nickel-based superalloys (Inconel, Hastelloy)',
    subclasses: [],
  },
  { id: 'titanium_alloy', label: 'Titanium alloys', subclasses: [] },
  { id: 'magnesium_alloy', label: 'Magnesium alloys', subclasses: [] },
  { id: 'zinc_alloy', label: 'Zinc alloys', subclasses: [] },
  OTHER_MATERIAL_CLASS,
];

export function materialClassesFor(family: MaterialClassFamily): readonly MaterialClassDef[] {
  return family === 'metal' ? METAL_MATERIAL_CLASSES : POLYMER_MATERIAL_CLASSES;
}

export function findMaterialClass(
  family: MaterialClassFamily,
  classId: string,
): MaterialClassDef | undefined {
  return materialClassesFor(family).find((c) => c.id === classId);
}

/**
 * One material the supplier declared for a process. `subclassId` is null until
 * the class grows subclasses and the supplier picks one; `customLabel` is only
 * meaningful on `other_exotic`, where it is the material's name.
 */
export interface MaterialClassSelection {
  classId: string;
  subclassId: string | null;
  customLabel: string | null;
}

export const materialClassSelectionSchema = z.object({
  classId: z.string().trim().min(1).max(64),
  subclassId: z.string().trim().min(1).max(64).nullish(),
  customLabel: z.string().trim().min(1).max(120).nullish(),
});

/**
 * Whether a selection names a real class in this family (and, if given, a real
 * subclass of it). `other_exotic` additionally requires the free-text name —
 * "Other" on its own says nothing a buyer can search — and only `other_exotic`
 * may carry one: every other class renders from its own label, so a free-text
 * name there would be stored and never shown.
 */
export function isValidMaterialClassSelection(
  family: MaterialClassFamily,
  selection: MaterialClassSelection,
): boolean {
  const def = findMaterialClass(family, selection.classId);
  if (!def) return false;
  if (selection.classId === OTHER_MATERIAL_CLASS_ID) {
    if (!selection.customLabel?.trim()) return false;
  } else if (selection.customLabel?.trim()) {
    return false;
  }
  if (selection.subclassId && !def.subclasses.some((s) => s.id === selection.subclassId)) {
    return false;
  }
  return true;
}

/** What to show for a selection — the free text for Other, the class label otherwise. */
export function materialClassLabel(
  family: MaterialClassFamily,
  selection: MaterialClassSelection,
): string {
  if (selection.classId === OTHER_MATERIAL_CLASS_ID) {
    return selection.customLabel?.trim() || 'Other / Exotic';
  }
  const def = findMaterialClass(family, selection.classId);
  if (!def) return selection.classId;
  const sub = def.subclasses.find((s) => s.id === selection.subclassId);
  return sub ? `${def.label} — ${sub.label}` : def.label;
}

/**
 * Which structured form a process asks for once it is selected. `casting` adds
 * shot volume and clamping force to the shared bounding-box + material-class
 * core; `forming` is the core alone. Subtractive gets its own value when
 * Rashid's dataset lands.
 */
export const CAPABILITY_SPEC_FORMS = ['casting', 'forming'] as const;

export type CapabilitySpecForm = (typeof CAPABILITY_SPEC_FORMS)[number];

export const capabilitySpecFormSchema = z.enum(CAPABILITY_SPEC_FORMS);
